import { useMutation } from '@tanstack/react-query';
import { AuthContext, Button, Dialog, Input } from 'alurkerja-ui';
import moment from 'moment';
import { ReactNode, useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FieldValues, useForm } from 'react-hook-form';

import { ChevronDown } from 'lucide-react';
import { MyTaskContext } from './MyTask';

type Approval = 'DITERIMA' | 'DITOLAK' | 'REVISI';

interface ApprovalProps {
  data: any;
  approveBy?: 'TRIBE_LEAD' | 'PG';
  renderAction?: () => ReactNode;
}

export const Approval = ({ data, approveBy, renderAction }: ApprovalProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const axiosInstance = useContext(AuthContext);
  const { resetSelectedTask } = useContext(MyTaskContext);
  const { register, getValues } = useForm();

  const [showPreview, setShowPreview] = useState<'open' | 'closed'>('closed');

  const { mutate, isLoading: isLoadingApprove } = useMutation({
    mutationFn: (payload: FieldValues) => {
      const body: {
        approval_circle_lead?: string;
        approval_people_growth?: string;
        keterangan?: string;
        approval_circle_lead_keterangan?: string;
        approval_people_growth_keterangan?: string;
      } = {};
      if (approveBy === 'TRIBE_LEAD') {
        body.approval_circle_lead = payload.approval;
        body.approval_circle_lead_keterangan = payload.keterangan;
      } else {
        body.approval_people_growth = payload.approval;
        body.approval_people_growth_keterangan = payload.keterangan;
      }

      // body.keterangan = payload.keterangan

      return axiosInstance.post(
        `/workflow/v1/bpmn/${params.bpmnProcessId}/${params.instanceID}/task/${data?.tasks[0].key}/submit`,
        body,
      );
    },
    onSuccess: res => {
      const data = res.data.data;
      Dialog.success({
        title: 'Berhasil',
        description: 'Pengajuan berhasil ' + data.action,
        callback: () => {
          resetSelectedTask?.();
          navigate('/my-task');
        },
      });
    },
  });

  return (
    <div>
      <details
        open={showPreview === 'open'}
        onToggle={(e: any) => setShowPreview(e.nativeEvent.newState)}
        data-testid="details"
      >
        <summary className="flex list-none border-b">
          <div className="w-full ">
            <div className="py-4 border-b">
              <div className="relative font-bold text-black-alurkerja-2">
                Detail
                <div className="absolute top-0 right-0 translate-y-1/2">
                  <ChevronDown size={12} />
                </div>
              </div>
            </div>
            {showPreview === 'closed' && (
              <div className="w-full py-4 text-center text-gray-alurkerja-1 text-[10px] cursor-pointer"
                onClick={() => {
                  setShowPreview('open');
                }}
                data-testid="show-detail"
              >
                Klik untuk Melihat Detail
              </div>
            )}
          </div>
        </summary>

        <div>
          <div className="grid w-4/5 grid-cols-2 gap-4 py-4">
            <div>
              <label htmlFor="">Jenis Pengajuan</label>
              <Input value={data?.jenis_pengajuan} />
            </div>
            <div>
              <label htmlFor="">Tanggal</label>
              <Input value={moment(data?.tanggal, 'DD/MM/YYYY').format('DD MMMM YYYY')} />
            </div>
            <div>
              <label htmlFor="">Jam Mulai</label>
              <Input value={data?.jam_mulai} />
            </div>
            <div>
              <label htmlFor="">Jam Selesai</label>
              <Input value={data?.jam_selesai} />
            </div>
            <div className="col-span-2">
              <label htmlFor="">Keterangan</label>
              <Input value={data?.keterangan} textArea />
            </div>
            {data?.jenis_pengajuan === 'SAKIT' && (
              <div className="col-span-2">
                <label htmlFor="">Foto Surat Dokter</label>
                <img
                  className="w-auto h-[331px] object-cover bg-gray-50 rounded"
                  src={data?.dokumen_pendukung}
                  alt=""
                />
              </div>
            )}
          </div>
          {showPreview === 'open' && (
            <div
              className="w-full text-center text-gray-alurkerja-1 text-[10px] pb-4 cursor-pointer border-b"
              onClick={() => {
                setShowPreview('closed');
              }}
              data-testid="hide-detail"
            >
              Klik untuk Menyembunyikan Detail
            </div>
          )}
        </div>
      </details>

      <div>
        <div className="py-4 mb-4 font-bold text-gray-700 border-b">Aksi</div>
        {renderAction ? (
          renderAction()
        ) : (
          <>
            <div className="mb-4">
              <label htmlFor="">Keterangan</label>
              <Input {...register('keterangan')} textArea data-testid="input-ket" />
            </div>
            <div className="flex justify-between w-full">
              <div className="flex gap-2.5">
                {approveBy !== 'PG' && (
                  <Button
                    onClick={() => mutate({ approval: 'REVISI', keterangan: getValues('keterangan') })}
                    color="red"
                    loading={isLoadingApprove}
                  >
                    Revisi
                  </Button>
                )}

                <Button
                  onClick={() => mutate({ approval: 'DITOLAK', keterangan: getValues('keterangan') })}
                  loading={isLoadingApprove}
                  color="red"
                >
                  Tolak
                </Button>
              </div>
              <Button
                onClick={() => mutate({ approval: 'DITERIMA', keterangan: getValues('keterangan') })}
                loading={isLoadingApprove}
              >
                Terima
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
